import { Modal, Form, Input, InputNumber, Select, Row, Col, Divider, Button, Space, Typography, Tag, List, Checkbox } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { currencyOptions } from '../constants';
import type { CustomerInsuranceType, InsuranceType } from '../types';

const { Text } = Typography;

export type AgreementFormValues = {
  company_name: string;
  cvrnummer: string;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  industry?: string | null;
  annual_value?: number | null;
  commission_percent?: number | null;
  commission_amount?: number | null;
  currency?: string | null;
  contract_signed_at?: string | null;
  contract_starts_at?: string | null;
  contract_expires_at?: string | null;
  renewal_notice_at?: string | null;
  notes?: string | null;
};

type AgreementModalProps = {
  open: boolean;
  loading: boolean;
  submitting: boolean;
  initialValues: Partial<AgreementFormValues>;
  insuranceTypes?: CustomerInsuranceType[];
  availableInsuranceTypes?: InsuranceType[];
  selectedInsuranceTypeIds?: string[];
  onToggleInsuranceType?: (id: string, checked: boolean) => void;
  onCancel: () => void;
  onSubmit: (values: AgreementFormValues) => void | Promise<void>;
  onSaveDraft?: (values: AgreementFormValues) => void | Promise<void>;
  saveDraftLoading?: boolean;
};

export function AgreementModal({
  open,
  loading,
  submitting,
  initialValues,
  insuranceTypes = [],
  availableInsuranceTypes = [],
  selectedInsuranceTypeIds = [],
  onToggleInsuranceType,
  onCancel,
  onSubmit,
  onSaveDraft,
  saveDraftLoading
}: AgreementModalProps) {
  const [form] = Form.useForm<AgreementFormValues>();
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (open) {
      const normalized: Partial<AgreementFormValues> = {
        currency: 'DKK',
        ...initialValues,
        primary_contact_name: initialValues.primary_contact_name ?? '',
        primary_contact_email: initialValues.primary_contact_email ?? '',
        primary_contact_phone: initialValues.primary_contact_phone ?? '',
        address: initialValues.address ?? '',
        postal_code: initialValues.postal_code ?? '',
        city: initialValues.city ?? '',
        country: initialValues.country ?? '',
        industry: initialValues.industry ?? '',
        notes: initialValues.notes ?? ''
      };
      form.resetFields();
      form.setFieldsValue(normalized);
    }
  }, [form, initialValues, open]);

  const selectedItems = useMemo(
    () =>
      availableInsuranceTypes.filter((item) => selectedInsuranceTypeIds.includes(item.id)).map((item) => ({
        ...item,
        insuranceTypeId: item.id
      })),
    [availableInsuranceTypes, selectedInsuranceTypeIds]
  );

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => onSubmit(values))
      .catch(() => undefined);
  };

  const handleSaveDraft = () => {
    if (!onSaveDraft) return;
    const values = form.getFieldsValue(true) as AgreementFormValues;
    onSaveDraft(values);
  };

  return (
    <Modal
      open={open}
      title="Agreement"
      okText="Gem og opdater status"
      cancelText="Annuller"
      confirmLoading={submitting}
      onCancel={onCancel}
      footer={
        <Space>
          <Button onClick={onCancel}>Annuller</Button>
          {onSaveDraft ? (
            <Button loading={saveDraftLoading} onClick={handleSaveDraft}>
              Gem som kladde
            </Button>
          ) : null}
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Gem og opdater status
          </Button>
        </Space>
      }
      destroyOnClose
      width={720}
      maskClosable={false}
    >
      <Form
        layout="vertical"
        form={form}
        preserve={false}
        initialValues={{
          currency: 'DKK',
          ...initialValues
        }}
      >
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item
              label="Virksomhedsnavn"
              name="company_name"
              rules={[{ required: true, message: 'Angiv virksomhedsnavn' }]}
            >
              <Input disabled={loading} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="CVR"
              name="cvrnummer"
              rules={[{ required: true, message: 'Angiv CVR' }]}
            >
              <Input disabled={loading} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          Kontakt
        </Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              label="Navn"
              name="primary_contact_name"
              rules={[{ required: true, message: 'Angiv kontaktperson' }]}
            >
              <Input disabled={loading} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="E-mail"
              name="primary_contact_email"
              rules={[
                { required: true, message: 'Angiv e-mail' },
                { type: 'email', message: 'Angiv gyldig e-mail' }
              ]}
            >
              <Input disabled={loading} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Telefon" name="primary_contact_phone" rules={[{ required: true, message: 'Angiv telefon' }]}>
              <Input disabled={loading} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          Adresse
        </Divider>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Adresse" name="address" rules={[{ required: true, message: 'Angiv adresse' }]}>
              <Input disabled={loading} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Postnr." name="postal_code" rules={[{ required: true, message: 'Angiv postnr.' }]}>
              <Input disabled={loading} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="By" name="city" rules={[{ required: true, message: 'Angiv by' }]}>
              <Input disabled={loading} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Land" name="country">
              <Input disabled={loading} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Branche" name="industry" rules={[{ required: true, message: 'Angiv branche' }]}>
              <Input disabled={loading} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          Økonomi og kontrakt
        </Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Årlig værdi" name="annual_value" rules={[{ required: true, message: 'Angiv årlig værdi' }]}>
              <InputNumber style={{ width: '100%' }} disabled={loading} min={0} step={1000} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Provision (%)"
              name="commission_percent"
              rules={[{ required: true, message: 'Angiv provision %' }]}
            >
              <InputNumber style={{ width: '100%' }} disabled={loading} min={0} max={100} step={0.5} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Valuta" name="currency" rules={[{ required: true, message: 'Vælg valuta' }]}>
              <Select options={currencyOptions} disabled={loading} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item label="Underskrevet" name="contract_signed_at" rules={[{ required: true, message: 'Angiv underskrevet dato' }]}>
              <Input type="date" disabled={loading} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Start" name="contract_starts_at" rules={[{ required: true, message: 'Angiv startdato' }]}>
              <Input type="date" disabled={loading} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Udløb" name="contract_expires_at" rules={[{ required: true, message: 'Angiv udløbsdato' }]}>
              <Input type="date" disabled={loading} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Fornyelse" name="renewal_notice_at" rules={[{ required: true, message: 'Angiv fornyelsesdato' }]}>
              <Input type="date" disabled={loading} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          <Space align="center" size={8}>
            <span>Forsikringstyper</span>
            <Button
              icon={<PlusOutlined />}
              size="small"
              onClick={() => setPickerOpen(true)}
              disabled={loading || submitting || availableInsuranceTypes.length === 0}
            >
              Tilføj / rediger
            </Button>
          </Space>
        </Divider>
        {selectedItems.length === 0 ? (
          <Text type="secondary">Ingen forsikringstyper tilknyttet.</Text>
        ) : (
          <List
            size="small"
            dataSource={selectedItems}
            renderItem={(item: CustomerInsuranceType) => (
              <List.Item
                key={item.insuranceTypeId}
                actions={[
                  <Button
                    key="remove"
                    type="text"
                    danger
                    size="small"
                    onClick={() => onToggleInsuranceType?.(item.id, false)}
                    disabled={loading || submitting}
                  >
                    Fjern
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space size={8} wrap>
                      <Text strong>{item.title || item.insuranceTypeId}</Text>
                      {item.category ? <Tag>{item.category}</Tag> : null}
                      {item.mandatory ? <Tag color="red">Lovpligtig</Tag> : null}
                    </Space>
                  }
                  description={item.description ? <Text>{item.description}</Text> : <Text type="secondary">Ingen beskrivelse</Text>}
                />
              </List.Item>
            )}
          />
        )}

        <Form.Item label="Noter" name="notes">
          <Input.TextArea rows={3} disabled={loading} />
        </Form.Item>
      </Form>

      <Modal
        title="Tilknyt forsikringstyper"
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        onOk={() => setPickerOpen(false)}
        okText="Luk"
        cancelButtonProps={{ style: { display: 'none' } }}
        destroyOnClose
      >
        {availableInsuranceTypes.length === 0 ? (
          <Text type="secondary">Ingen forsikringstyper fundet.</Text>
        ) : (
          <List
            size="small"
            dataSource={availableInsuranceTypes}
            renderItem={(item: InsuranceType) => (
              <List.Item key={item.id}>
                <List.Item.Meta
                  title={
                    <Space size={8} wrap>
                      <Checkbox
                        checked={selectedInsuranceTypeIds.includes(item.id)}
                        onChange={(e) => onToggleInsuranceType?.(item.id, e.target.checked)}
                        disabled={loading || submitting}
                      >
                        {item.title}
                      </Checkbox>
                      {item.category ? <Tag>{item.category}</Tag> : null}
                      {item.mandatory ? <Tag color="red">Lovpligtig</Tag> : null}
                    </Space>
                  }
                  description={item.description ? <Text>{item.description}</Text> : <Text type="secondary">Ingen beskrivelse</Text>}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </Modal>
  );
}
